import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bookmark, Trash2, Download, Upload, Link2, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import {
  loadViews,
  deleteView,
  toHref,
  downloadViewsFile,
  importViews,
  buildShareUrl,
} from '../../lib/savedViews';
import Tooltip from './Tooltip';
import ShareQrModal from './ShareQrModal';

/**
 * Sezione della Sidebar che elenca le viste salvate (filter presets).
 *
 * Si aggiorna automaticamente quando un'altra parte dell'app chiama
 * `addView` / `deleteView` (eventi `qp:saved-views-changed` + storage event).
 *
 * In modalità collapsed mostra solo l'icona bookmark con tooltip.
 */
export default function SavedViews({ collapsed }) {
  const loc = useLocation();
  const [views, setViews] = useState(() => loadViews());
  const fileInputRef = useRef(null);
  const [qr, setQr] = useState(null); // { title, url } | null

  useEffect(() => {
    const refresh = () => setViews(loadViews());
    window.addEventListener('qp:saved-views-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('qp:saved-views-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const handleExport = () => {
    if (views.length === 0) {
      toast.info('Nessuna vista da esportare');
      return;
    }
    downloadViewsFile();
    toast.success(`Esportate ${views.length} viste`);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset così lo stesso file può essere ricaricato
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const existing = loadViews().length;
      let mode = 'merge';
      if (existing > 0) {
        const replace = window.confirm(
          `Hai già ${existing} viste salvate.\n\n` +
          `OK = Sostituisci tutto con il file importato\n` +
          `Annulla = Unisci (mantieni le viste esistenti, aggiungi solo le nuove)`
        );
        mode = replace ? 'replace' : 'merge';
      }
      const result = importViews(payload, mode);
      if (mode === 'replace') {
        toast.success(`Sostituito: ${result.total} viste`);
      } else {
        toast.success(
          result.skipped > 0
            ? `Importate ${result.added} viste (${result.skipped} già presenti)`
            : `Importate ${result.added} viste`
        );
      }
    } catch (err) {
      toast.error(`Import fallito: ${err.message || 'file non valido'}`);
    }
  };

  const isActive = (v) => {
    if (loc.pathname !== v.route) return false;
    return loc.search.replace(/^\?/, '') === v.search;
  };

  if (collapsed) {
    if (views.length === 0) return null;
    return (
      <div className="mt-2 px-2" data-testid="saved-views-collapsed">
        <Tooltip label={`Viste salvate · ${views.length}`} side="right">
          <span className="inline-flex items-center justify-center w-9 h-9 text-soft">
            <Bookmark size={14} strokeWidth={1.5} />
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="mt-3 px-1" data-testid="saved-views">
      <div className="px-2 mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mute flex items-center justify-between gap-1.5">
        <span className="flex items-center gap-1.5 min-w-0 truncate">
          <Bookmark size={11} strokeWidth={1.5} /> Viste salvate · {views.length}
        </span>
        <span className="flex items-center gap-0.5 shrink-0">
          <Tooltip label="Esporta tutte le viste in un file .json (backup)" side="top">
            <button
              type="button"
              onClick={handleExport}
              className="p-1 text-mute hover:text-ink transition-colors"
              data-testid="saved-views-export-btn"
              aria-label="Esporta viste in JSON"
            >
              <Download size={11} strokeWidth={1.5} />
            </button>
          </Tooltip>
          <Tooltip label="Importa da un file .json esportato in precedenza" side="top">
            <button
              type="button"
              onClick={handleImportClick}
              className="p-1 text-mute hover:text-ink transition-colors"
              data-testid="saved-views-import-btn"
              aria-label="Importa viste da JSON"
            >
              <Upload size={11} strokeWidth={1.5} />
            </button>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFileChange}
            className="hidden"
            data-testid="saved-views-import-input"
          />
        </span>
      </div>
      {views.length === 0 ? (
        <div
          className="px-3 py-2 font-body text-[12px] text-mute leading-snug space-y-1"
          data-testid="saved-views-empty"
        >
          <p>Nessuna vista salvata ancora.</p>
          <p>
            <span className="text-soft">Per crearne una:</span> vai in una pagina con filtri
            (es. <span className="font-mono text-[11px]">Obiettivi</span>,{' '}
            <span className="font-mono text-[11px]">Settimana</span>), applica i filtri e clicca{' '}
            <span className="font-mono text-[11px] whitespace-nowrap">« Salva vista »</span> in alto.
          </p>
          <p className="text-mute italic">
            Oppure clicca <Upload size={9} strokeWidth={1.5} className="inline" /> qui sopra per importare un backup.
          </p>
        </div>
      ) : (
        <ul className="space-y-0.5">
          {views.map((v) => {
            const active = isActive(v);
            return (
              <li
                key={v.id}
                className="group flex items-center"
                data-testid={`saved-view-${v.id}`}
              >
                <Link
                  to={toHref(v)}
                  className={
                    'flex-1 min-w-0 px-3 py-1.5 font-body text-[13px] truncate transition-colors ' +
                    (active
                      ? 'bg-ink text-canvas'
                      : 'text-soft hover:bg-linesoft hover:text-ink')
                  }
                  title={`${v.route}${v.search ? '?' + v.search : ''}`}
                  data-testid={`saved-view-link-${v.id}`}
                >
                  {v.name}
                </Link>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const url = buildShareUrl(v);
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success('Link vista copiato', { description: url });
                    } catch {
                      // Fallback per browser senza Clipboard API
                      window.prompt('Copia il link:', url);
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 px-1 text-mute hover:text-ink transition-opacity"
                  title="Condividi via link"
                  data-testid={`saved-view-share-${v.id}`}
                >
                  <Link2 size={11} strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setQr({ title: v.name, url: buildShareUrl(v) });
                  }}
                  className="opacity-0 group-hover:opacity-100 px-1 text-mute hover:text-ink transition-opacity"
                  title="Mostra QR"
                  data-testid={`saved-view-qr-${v.id}`}
                >
                  <QrCode size={11} strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (window.confirm(`Eliminare la vista "${v.name}"?`)) deleteView(v.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 px-2 text-mute hover:text-accent transition-opacity"
                  title="Elimina vista"
                  data-testid={`saved-view-delete-${v.id}`}
                >
                  <Trash2 size={11} strokeWidth={1.5} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ShareQrModal
        open={!!qr}
        onClose={() => setQr(null)}
        title={qr?.title || ''}
        url={qr?.url || ''}
      />
    </div>
  );
}
